import React, { useEffect } from 'react';
import { Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import withRouter from 'Common/withRouter';
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import { requestLoginOtp, resetLoginFlag, verifyLoginOtp } from "../../slices/thunk";
import { createSelector } from 'reselect';
import logo from "../../assets/images/admin-logos/Sfl-login-logo.png";
import loginBg from "../../assets/images/auth/Login-bg.png";
import { decrementOtpCooldown, resetOtpLogin } from "../../slices/auth/login/reducer";
import './Login.css';

const Signin = (props: any) => {
    const dispatch: any = useDispatch();
    const selectAccountAndLogin = createSelector(
        (state: any) => state.Login,
        (login) => ({
            error: login.error,
            authErrorField: login.authErrorField,
            loading: login.loading,
            errorMsg: login.errorMsg,
            otpStep: login.otpStep,
            otpEmail: login.otpEmail,
            otpPolicy: login.otpPolicy,
            resendsRemaining: login.resendsRemaining,
            resendSecondsRemaining: login.resendSecondsRemaining,
            otpAttemptsBlocked: login.otpAttemptsBlocked,
        })
    );

    const { error, authErrorField, loading, errorMsg, otpStep, otpEmail, otpPolicy, resendsRemaining, resendSecondsRemaining, otpAttemptsBlocked } = useSelector(selectAccountAndLogin);

    // Debug logging
    console.log('Login component state:', { error, loading, errorMsg });

    const otpEmailForm: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: otpEmail || '',
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Please enter a valid email").required("Please Enter Your Email"),
        }),
        onSubmit: (values) => {
            dispatch(requestLoginOtp(values.email));
        }
    });

    const otpVerifyForm: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            otp: '',
        },
        validationSchema: Yup.object({
            otp: Yup.string().matches(/^\d{6}$/, "Enter the 6-digit OTP").required("Please Enter OTP"),
        }),
        onSubmit: (values, helpers) => {
            dispatch(verifyLoginOtp({ email: otpEmail, otp: values.otp }, props.router.navigate));
            helpers.setSubmitting(false);
        }
    });
    useEffect(() => {
        if (errorMsg && !otpAttemptsBlocked) {
            setTimeout(() => {
                dispatch(resetLoginFlag());
            }, 8000); // Increased from 3000ms to 8000ms to give users more time to read error messages
        }
    }, [dispatch, errorMsg, otpAttemptsBlocked]);

    useEffect(() => {
        if (otpStep !== 'otp' || !resendSecondsRemaining) return;

        const timer = window.setInterval(() => {
            dispatch(decrementOtpCooldown());
        }, 1000);

        return () => window.clearInterval(timer);
    }, [dispatch, otpStep, resendSecondsRemaining]);

    // Check if form is valid for button disable state
    const isOtpEmailValid = otpEmailForm.values.email.trim() !== '';
    const isOtpValid = /^\d{6}$/.test(otpVerifyForm.values.otp || '');
    const canResend = otpStep === 'otp' && resendSecondsRemaining <= 0 && resendsRemaining !== 0;
    const serverError = typeof error === 'string' ? error : '';
    const otpEmailServerError = otpStep === 'email' && authErrorField === 'email' ? serverError : '';
    const otpCodeServerError = otpStep === 'otp' && authErrorField === 'otp' ? serverError : '';
    const shouldShowGlobalError = error && !otpEmailServerError && !otpCodeServerError;

    const handleDifferentEmail = () => {
        dispatch(resetOtpLogin());
        otpVerifyForm.resetForm();
    };

    const handleResend = () => {
        if (!otpEmail || !canResend) return;
        otpVerifyForm.resetForm();
        dispatch(requestLoginOtp(otpEmail));
    };

    // Title is now handled by the dynamic title system
    return (
        <div
            style={{
                backgroundImage: `url(${loginBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                minWidth: '100vw',
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                padding: 0,
            }}
        >
            <Card className="border-0 shadow-none" style={{ background: 'rgba(255,255,255,0.95)', minWidth: 400, maxWidth: 500, width: '100%' }}>
                <Card.Body className="p-sm-5 m-lg-4">
                    <div className="text-center mb-4">
                        <img src={logo} alt="SFL Logo" style={{ height: 70, marginBottom: 16 }} />
                        <h5 className="fs-3xl mt-3 mb-2">Welcome to SFL Admin</h5>
                        <p className="text-muted">
                            {otpStep === 'otp'
                                ? `Enter the 6-digit code sent to ${otpEmail}`
                                : 'Enter your email ID to receive a login code'}
                        </p>
                    </div>
                    <div className="p-2">
                        {shouldShowGlobalError ? (
                            <Alert
                                variant="danger"
                                dismissible
                                onClose={() => dispatch(resetLoginFlag())}
                            >
                                {error}
                            </Alert>
                        ) : null}
                        {otpStep === 'email' && <Form action="#" onSubmit={(e) => {
                            e.preventDefault(); otpEmailForm.handleSubmit();
                            return false;
                        }}>
                            <Form.Group className="mb-3" controlId="otpEmail">
                                <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    className="form-control login-form-control"
                                    placeholder="Type your mail ID here.."
                                    required
                                    onChange={otpEmailForm.handleChange}
                                    onBlur={otpEmailForm.handleBlur}
                                    value={otpEmailForm.values.email || ""}
                                    isInvalid={!!((otpEmailForm.touched.email && otpEmailForm.errors.email) || otpEmailServerError)}
                                />
                                {(otpEmailForm.touched.email && otpEmailForm.errors.email) || otpEmailServerError ? (
                                    <Form.Control.Feedback type="invalid">{otpEmailForm.errors.email || otpEmailServerError}</Form.Control.Feedback>
                                ) : null}
                            </Form.Group>
                            <div className="mt-4">
                                <Button
                                    className={`btn w-100 login-btn ${!isOtpEmailValid ? 'btn-disabled' : 'btn-primary'}`}
                                    type="submit"
                                    disabled={loading || !isOtpEmailValid}
                                >
                                    {loading && <Spinner size='sm' />} {" "}Send OTP
                                </Button>
                            </div>
                        </Form>}

                        {otpStep === 'otp' && <Form action="#" onSubmit={(e) => {
                            e.preventDefault(); otpVerifyForm.handleSubmit();
                            return false;
                        }}>
                            <Form.Group className="mb-3" controlId="otpCode">
                                <Form.Label>OTP <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    name="otp"
                                    className="form-control login-form-control"
                                    placeholder="Enter 6-digit OTP"
                                    maxLength={6}
                                    required
                                    onChange={(event) => {
                                        const nextValue = event.target.value.replace(/\D/g, '').slice(0, 6);
                                        otpVerifyForm.setFieldValue('otp', nextValue);
                                    }}
                                    onBlur={otpVerifyForm.handleBlur}
                                    value={otpVerifyForm.values.otp || ""}
                                    disabled={loading || otpAttemptsBlocked}
                                    isInvalid={!!((otpVerifyForm.touched.otp && otpVerifyForm.errors.otp) || otpCodeServerError)}
                                />
                                {otpCodeServerError || (otpVerifyForm.touched.otp && otpVerifyForm.errors.otp) ? (
                                    <Form.Control.Feedback type="invalid">{otpCodeServerError || otpVerifyForm.errors.otp}</Form.Control.Feedback>
                                ) : null}
                            </Form.Group>
                            {otpPolicy?.expiresInSeconds ? (
                                <div className="text-muted small mb-3">
                                    Code expires in {Math.ceil(otpPolicy.expiresInSeconds / 60)} minutes.
                                </div>
                            ) : null}
                            <div className="mt-4">
                                <Button
                                    className={`btn w-100 login-btn ${!isOtpValid ? 'btn-disabled' : 'btn-primary'}`}
                                    type="submit"
                                    disabled={loading || !isOtpValid || otpAttemptsBlocked}
                                >
                                    {loading && <Spinner size='sm' />} {" "}Verify
                                </Button>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                {resendsRemaining === 0 ? (
                                    <span className="text-muted small">You’ve reached the maximum OTP resend limit. Please contact us at contact@sportsforlife.in</span>
                                ) : (
                                    <Button
                                        variant="link"
                                        className="text-decoration-none p-0"
                                        type="button"
                                        disabled={loading || !canResend}
                                        onClick={handleResend}
                                    >
                                        {resendSecondsRemaining > 0 ? `Resend in ${resendSecondsRemaining}s` : 'Resend code'}
                                    </Button>
                                )}
                                <Button variant="link" className="text-decoration-none p-0" type="button" onClick={handleDifferentEmail}>
                                    Use a different email
                                </Button>
                            </div>
                        </Form>}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default withRouter(Signin);
